import { AssistiveMmlHandler } from '@mathjax/src/js/a11y/assistive-mml.js';
import { liteAdaptor } from '@mathjax/src/js/adaptors/liteAdaptor.js';
import { RegisterHTMLHandler } from '@mathjax/src/js/handlers/html.js';
import { MathML } from '@mathjax/src/js/input/mathml.js';
import { TeX } from '@mathjax/src/js/input/tex.js';
import { mathjax } from '@mathjax/src/js/mathjax.js';
import { CHTML } from '@mathjax/src/js/output/chtml.js';
import { SVG } from '@mathjax/src/js/output/svg.js';

// TeX packages
import '@mathjax/src/js/input/tex/ams/AmsConfiguration.js';
import '@mathjax/src/js/input/tex/base/BaseConfiguration.js';
import '@mathjax/src/js/input/tex/color/ColorConfiguration.js';
import '@mathjax/src/js/input/tex/configmacros/ConfigMacrosConfiguration.js';
import '@mathjax/src/js/input/tex/mathtools/MathtoolsConfiguration.js';
import '@mathjax/src/js/input/tex/require/RequireConfiguration.js';

// Manually load font files
import '@mathjax/mathjax-newcm-font/js/chtml/dynamic/double-struck.js';
import '@mathjax/mathjax-newcm-font/js/chtml/dynamic/script.js';

// Override to skip dynamic loading of font files in the convertPromise call
// https://github.com/mathjax/MathJax-src/blob/1a2ef74c0ac0620e7b8de46402c9dce3b95ade52/ts/util/asyncLoad/esm.ts#L32
mathjax.asyncLoad = async () => {
    return;
};

const defaultConfig = {
    fontUrl: 'https://cdn.jsdelivr.net/npm/@mathjax/mathjax-newcm-font@4.0.0/chtml/woff2', // To replace with custom font from zybooks
    texOptions: {
        packages: { '[+]': ['base', 'ams', 'mathtools', 'configmacros', 'require', 'color'] },
        macros: {
            True: '{\\text{T}}',
            False: '{\\text{F}}',
        },
        processEscapes: false,
    },
};

export interface TexToHtmlOptions {
    inputFormat?: 'tex' | 'mathml';

    /** Output format: 'html' or 'svg' (default: 'html') */
    outputFormat?: 'html' | 'svg';

    /** Process as inline math (default: false) */
    inline?: boolean;

    /** em-size in pixels (default: 16) */
    em?: number;

    /** ex-size in pixels (default: 8) */
    ex?: number;

    /** width of container in pixels (default: 1280) */
    width?: number;

    /** TeX packages to load */
    texOptions?: Record<string, any>;

    /** output the required CSS rather than the HTML itself (default: false) */
    css?: boolean;

    /** the URL to use for web fonts */
    fontURL?: string;

    /** whether to include assistive MathML output (default: true) */
    assistiveMml?: boolean;

    /** whether to use local font cache for SVG output (default: false) */
    fontCache?: boolean;
}

export interface TexToHtmlResult {
    html: string;
    css: string | null;
}

/**
 * Converts a TeX string or MathML to HTML using MathJax v4
 * @param texString The TeX string to convert
 * @param options Configuration options
 * @returns Object containing the output HTML/CSS
 */
export async function tex2html(texString: string, options: TexToHtmlOptions = {}): Promise<TexToHtmlResult> {
    const {
        inputFormat = 'tex',
        outputFormat = 'html',
        inline = false,
        em = 16,
        ex = 8,
        width = 80 * 16,
        texOptions = defaultConfig.texOptions,
        css = false,
        fontURL = defaultConfig.fontUrl,
        assistiveMml = true,
        fontCache = true,
    } = options;

    // Create DOM adaptor and register it for HTML documents
    const adaptor = liteAdaptor();
    const handler = RegisterHTMLHandler(adaptor);
    if (assistiveMml) {
        AssistiveMmlHandler(handler as any);
    }

    // Create input and output jax and a document using them
    const tex = new TeX(texOptions || {});
    const mathml = new MathML();
    const chtml = new CHTML({ fontURL });
    const svg = new SVG({ fontCache: fontCache ? 'local' : 'none' });
    const outputJax = outputFormat == 'html' ? chtml : svg;
    const inputJax = inputFormat === 'mathml' ? mathml : tex;
    const html = mathjax.document('', { InputJax: inputJax, OutputJax: outputJax });

    const node = await html.convertPromise(texString, {
        display: !inline,
        em,
        ex,
        containerWidth: width,
    });

    return {
        html: adaptor.outerHTML(node),
        css: css ? adaptor.textContent(outputJax.styleSheet(html) as any) : null,
    };
}
